/**
 * Map Styles Configuration
 * Defines available Mapbox map styles for the layer panel
 */

export interface MapStyle {
  id: string
  label: string
  description: string
  styleUrl: string
}

export const POPETY_SATELLITE_MAP_STYLE_URL =
  'mapbox://styles/popety/cl449l1ah000414umwqgcr0pd'

const LEGACY_MAP_STYLE_IDS_BY_URL: Record<string, string> = {
  'mapbox://styles/mapbox/standard-satellite': 'mapbox-satellite',
}

/**
 * Available Mapbox map styles
 */
export const MAP_STYLES: MapStyle[] = [
  {
    id: 'popetyio-default',
    label: 'Popety.io',
    description: 'Popety.io map style for Switzerland',
    styleUrl: 'mapbox://styles/popety/cm7mg342r00u001s21uzzd25t', // Custom Popety street style
  },
  {
    id: 'mapbox-satellite',
    label: 'Satellite',
    description: 'Aerial imagery with street overlay',
    styleUrl: POPETY_SATELLITE_MAP_STYLE_URL,
  },
  {
    id: 'popety-zoning',
    label: 'Zoning',
    description: 'Custom style with zoning colors',
    styleUrl: 'mapbox://styles/popety/cmj2q4hv0004001s8fzwn4ljx', // Popety zoning style
  },
  {
    id: 'popety-zoning-satellite',
    label: 'Zoning Satellite',
    description: 'Satellite imagery with zoning overlay',
    styleUrl: 'mapbox://styles/popety/cmkzb8wo0000b01s58r9e6qvf', // Popety zoning satellite style
  },
  {
    id: 'mapbox-standard',
    label: 'Standard',
    description: '3D Building street map style',
    styleUrl: 'mapbox://styles/mapbox/standard',
  },
  {
    id: 'mapbox-traffic',
    label: 'Traffic',
    description: 'Map with traffic conditions',
    styleUrl: 'mapbox://styles/mapbox/traffic-day-v1',
  },
  {
    id: 'mapbox-outdoors',
    label: 'Outdoors',
    description: 'Outdoor map with terrain and hiking paths',
    styleUrl: 'mapbox://styles/mapbox/outdoors-v12',
  },
  {
    id: 'mapbox-light',
    label: 'Light',
    description: 'Minimal style for data visualization',
    styleUrl: 'mapbox://styles/mapbox/light-v11',
  },
]

/**
 * Default map style ID
 */
export const DEFAULT_MAP_STYLE_ID = 'popetyio-default'

/**
 * Get map style URL by ID
 */
export const getMapStyleUrl = (styleId: string): string => {
  const style = MAP_STYLES.find((s) => s.id === styleId)
  return style?.styleUrl || MAP_STYLES[0].styleUrl
}

/**
 * Get map style by ID
 */
export const getMapStyle = (styleId: string): MapStyle | undefined => {
  return MAP_STYLES.find((s) => s.id === styleId)
}

/**
 * Get map style ID from URL (reverse lookup)
 */
export const getMapStyleIdFromUrl = (styleUrl: string): string => {
  const style = MAP_STYLES.find((s) => s.styleUrl === styleUrl)
  return (
    style?.id || LEGACY_MAP_STYLE_IDS_BY_URL[styleUrl] || DEFAULT_MAP_STYLE_ID
  )
}
